import React, { useEffect, useState } from 'react';

import { Alert, Button, Checkbox, Empty, Flex, List, Spin, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useGetAttendanceQuery, useSaveAttendanceMutation } from '../api';
import type { AttendanceHistory } from '../types';

interface Props {
  bookingId: number;
  bookingKind: 'court-bookings' | 'coach-bookings';
  onRecordedChange?: (recorded: boolean) => void;
}

function historyAppearance(item: AttendanceHistory) {
  if (item.present === null) {
    return { color: 'default', label: 'admin-extensions.unknown', symbol: '–' };
  }

  return item.present
    ? { color: 'green', label: 'admin-extensions.present', symbol: '✓' }
    : { color: 'red', label: 'admin-extensions.absent', symbol: '✕' };
}

export function GroupAttendance({ bookingId, bookingKind, onRecordedChange }: Props) {
  const { t } = useTranslation();
  const [pollBeforeEnd, setPollBeforeEnd] = useState(true);
  const { data, isLoading, isFetching, isError, error } = useGetAttendanceQuery(
    { bookingId, bookingKind },
    { refetchOnMountOrArgChange: true, pollingInterval: pollBeforeEnd ? 30000 : 0 }
  );
  const [save, { isLoading: isSaving }] = useSaveAttendanceMutation();
  const [presentIds, setPresentIds] = useState<number[]>([]);

  useEffect(() => {
    if (data) {
      setPollBeforeEnd(!data.can_record);
      setPresentIds(data.participants.filter((participant) => participant.present).map((participant) => participant.user_id));
      onRecordedChange?.(data.recorded);
    } else if (isError) {
      setPollBeforeEnd(false);
    }
  }, [data, isError, onRecordedChange]);

  if (isLoading) {
    return <Spin />;
  }

  if (isError) {
    if ('status' in error && error.status === 404) {
      return null;
    }

    return <Alert type="error" showIcon message={t('admin-extensions.attendance-error')} />;
  }

  if (!data) {
    return null;
  }

  const hasEnded = data.can_record;
  const participants = data.participants;

  const handleSave = async () => {
    try {
      await save({ bookingId, bookingKind, userIds: presentIds }).unwrap();
      message.success(t('admin-extensions.attendance-saved'));
    } catch {
      message.error(t('admin-extensions.attendance-save-error'));
    }
  };

  return (
    <div style={{ marginTop: 20 }}>
      <Typography.Title level={5}>{t('admin-extensions.attendance')} — {data.group_name}</Typography.Title>
      {!hasEnded && <Alert type="info" message={t('admin-extensions.attendance-after')} showIcon />}
      {hasEnded && !data.recorded && <Alert type="info" message={t('admin-extensions.attendance-unrecorded')} showIcon />}
      {participants.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
        <List
          size="small"
          dataSource={participants}
          renderItem={(participant) => (
            <List.Item key={participant.user_id}>
              <Flex justify="space-between" align="center" style={{ width: '100%', gap: 8 }} wrap="wrap">
                <Checkbox
                  checked={presentIds.includes(participant.user_id)}
                  disabled={!hasEnded || isSaving || isFetching}
                  onChange={(event) => setPresentIds((current) => event.target.checked
                    ? [...current, participant.user_id]
                    : current.filter((id) => id !== participant.user_id))}
                >
                  <span style={{ opacity: data.recorded && !presentIds.includes(participant.user_id) ? 0.5 : 1 }}>
                    {participant.name}
                  </span>
                </Checkbox>
                <span aria-label={t('admin-extensions.recent-attendance')}>
                  {participant.recent.map((item) => {
                    const appearance = historyAppearance(item);
                    return (
                      <Tag
                        key={item.booking_id}
                        color={appearance.color}
                        title={`${dayjs(item.start_time).format('DD.MM.YYYY HH:mm')}: ${t(appearance.label)}`}
                      >
                        {appearance.symbol}
                      </Tag>
                    );
                  })}
                </span>
              </Flex>
            </List.Item>
          )}
        />
      )}
      {hasEnded && participants.length > 0 && (
        <Button type="primary" loading={isSaving} disabled={isFetching} onClick={() => {
          void handleSave();
        }} style={{ marginTop: 8 }}
        >
          {t('admin-extensions.save-attendance')}
        </Button>
      )}
    </div>
  );
}
